"use client"

import { useRef, useState, useCallback, useEffect } from "react"
import { motion } from "framer-motion"
import { useQuery } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import { useCharacterStore } from "@/features/characters/store"
import { useAuthStore } from "@/features/auth/store"
import { Input } from "@/shared/components/ui/input"
import { Button } from "@/shared/components/ui/button"
import { cn } from "@/shared/lib/utils"
import { Loader2, Pause, Play } from "lucide-react"
import { Icon } from "@/shared/components/icons"
import { Label } from "@/shared/components/ui/label"
import { Textarea } from "@/shared/components/ui/textarea"
import { generateBackstory } from "@/features/characters/actions"
import { toast } from "sonner"
import { createClient } from "@/shared/lib/supabase/client"
import { useUIStore } from "@/shared/stores/ui-store"
import { track } from "@/shared/lib/analytics"
import { isPaidTier } from "@/shared/lib/constants"
import { PremiumCustomOption } from "./premium-custom-option"
import type { CharacterPersonality } from "@/shared/types/character"
import type { VoiceTypeConfig } from "@/shared/types/ai-registry"
import {
  PERSONALITY_CHIP_DATA,
  OCCUPATION_OPTIONS,
  RELATIONSHIP_OPTIONS,
  HOBBY_OPTIONS,
  FETISH_OPTIONS,
  VOICE_FALLBACK_OPTIONS,
} from "./appearance-step"
import { pickRandomVoiceId } from "@/features/create/lib/random-character-defaults"
import { reconcileVoiceId } from "@/features/create/lib/reconcile-voice-id"
import { localizeCreateOptions } from "@/features/create/lib/create-option-labels"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu"

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChipOption {
  value: string
  label: string
  emoji?: string
}

interface Voice {
  id: string
  name: string
  description?: string | null
  previewUrl?: string | null
  accent?: string | null
  style?: string | null
}

// ─── Shared audio manager (stop all before playing a new one) ─────────────────

const activeAudio = { ref: null as HTMLAudioElement | null }

function stopAllAudio() {
  if (activeAudio.ref) {
    activeAudio.ref.pause()
    activeAudio.ref.currentTime = 0
  }
}

// ─── useAudioPlayer hook ──────────────────────────────────────────────────────

function useAudioPlayer(src: string | null) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ""
        audioRef.current = null
      }
    }
  }, [])

  const toggle = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!src) return

      if (isPlaying) {
        audioRef.current?.pause()
        setIsPlaying(false)
        return
      }

      // Stop whatever is currently playing globally
      stopAllAudio()
      setHasError(false)
      setIsLoading(true)

      try {
        if (!audioRef.current) {
          audioRef.current = new Audio()
          audioRef.current.addEventListener("ended", () => {
            setIsPlaying(false)
            activeAudio.ref = null
          })
          audioRef.current.addEventListener("error", () => {
            setIsPlaying(false)
            setIsLoading(false)
            setHasError(true)
            activeAudio.ref = null
          })
        }

        audioRef.current.src = src
        audioRef.current.load()
        activeAudio.ref = audioRef.current

        await audioRef.current.play()
        setIsPlaying(true)
      } catch {
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    },
    [src, isPlaying]
  )

  return { isPlaying, isLoading, hasError, toggle }
}

// ─── Voice style accent config (for dialog cards only) ───────────────────────

const VOICE_ACCENTS: Record<string, { dot: string; badge: string; ring: string }> = {
  soft:      { dot: "bg-violet-400", badge: "bg-violet-500/15 text-violet-300", ring: "ring-violet-500/40" },
  warm:      { dot: "bg-amber-400",  badge: "bg-amber-500/15 text-amber-300",   ring: "ring-amber-500/40" },
  sultry:    { dot: "bg-rose-400",   badge: "bg-rose-500/15 text-rose-300",     ring: "ring-rose-500/40" },
  playful:   { dot: "bg-pink-400",   badge: "bg-pink-500/15 text-pink-300",     ring: "ring-pink-500/40" },
  confident: { dot: "bg-blue-400",   badge: "bg-blue-500/15 text-blue-300",     ring: "ring-blue-500/40" },
  gentle:    { dot: "bg-emerald-400",badge: "bg-emerald-500/15 text-emerald-300",ring: "ring-emerald-500/40" },
  calm:      { dot: "bg-sky-400",    badge: "bg-sky-500/15 text-sky-300",       ring: "ring-sky-500/40" },
  energetic: { dot: "bg-yellow-400", badge: "bg-yellow-500/15 text-yellow-300", ring: "ring-yellow-500/40" },
  flirty:    { dot: "bg-fuchsia-400",badge: "bg-fuchsia-500/15 text-fuchsia-300",ring: "ring-fuchsia-500/40" },
}

function getAccent(style?: string | null) {
  return VOICE_ACCENTS[style ?? ""] ?? {
    dot: "bg-accent-blue",
    badge: "bg-accent-blue/15 text-accent-blue",
    ring: "ring-accent-blue/40",
  }
}

// ─── Chip (used inside dialogs) ───────────────────────────────────────────────

function Chip({
  value, label, emoji, isActive, isDisabled, onToggle,
}: {
  value: string; label: string; emoji?: string
  isActive: boolean; isDisabled: boolean; onToggle: (v: string) => void
}) {
  return (
    <motion.button
      type="button"
      onClick={() => !isDisabled && onToggle(value)}
      disabled={isDisabled}
      whileTap={isDisabled ? undefined : { scale: 0.92 }}
      animate={{ scale: isActive ? 1.05 : 1 }}
      transition={{ type: "spring", stiffness: 460, damping: 24 }}
      className={cn(
        "flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors duration-150 min-h-[40px]",
        isActive
          ? "bg-accent-blue/15 text-primary border border-accent-blue/50"
          : "border border-white/10 bg-white/[0.04] text-muted-foreground hover:border-primary/30 hover:text-foreground",
        isDisabled && "opacity-30 cursor-not-allowed"
      )}
    >
      {emoji && <span className="text-base leading-none">{emoji}</span>}
      {label}
    </motion.button>
  )
}

// ─── Attribute Card (flat, no gradient) ──────────────────────────────────────

function AttributeCard({
  label, value, placeholder, emoji, defaultIcon, isSelected, onClick, emptyLabel = "Not set",
}: {
  label: string; value?: string; placeholder?: string
  emoji?: string; defaultIcon?: React.ReactNode
  isSelected: boolean; onClick: () => void
  emptyLabel?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex w-full items-start justify-between rounded-xl sm:rounded-2xl border p-3 sm:p-4 text-left transition-all duration-200 min-h-[72px] sm:min-h-[86px]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        isSelected
          ? "border-accent-blue/50 bg-accent-blue/10"
          : "border-white/[0.07] bg-foreground/[0.03] hover:border-primary/25"
      )}
    >
      <div className="flex-1 min-w-0 pr-2">
        <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest mb-1 sm:mb-1.5 transition-colors text-muted-foreground/50">
          {label}
        </p>
        {isSelected && value ? (
          <p className="text-sm font-semibold text-white leading-snug truncate">{value}</p>
        ) : (
          <p className="text-xs text-muted-foreground/35 italic">{placeholder ?? emptyLabel}</p>
        )}
      </div>
      <div className="shrink-0 self-center ml-2">
        <span className={cn(
          "flex size-9 items-center justify-center rounded-xl text-lg transition-all duration-200",
          isSelected ? "opacity-100" : "opacity-25 group-hover:opacity-45"
        )}>
          {emoji ?? defaultIcon ?? <Icon name="arrow-right-2" size={16} className="size-4 text-muted-foreground" />}
        </span>
      </div>
    </button>
  )
}

// ─── Voice Attribute Card (flat, with play button) ────────────────────────────

function VoiceAttributeCard({
  selectedVoice, onClick, isInvalid, labels,
}: {
  selectedVoice?: Voice | null; onClick: () => void; isInvalid?: boolean
  labels: {
    title: string
    choose: string
    empty: string
    stopPreview: string
    playPreview: string
  }
}) {
  const src = selectedVoice?.previewUrl ?? null
  const { isPlaying, isLoading, toggle } = useAudioPlayer(src)
  const isSelected = !!selectedVoice
  const accent = getAccent(selectedVoice?.style)

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex w-full items-start justify-between rounded-xl sm:rounded-2xl border p-3 sm:p-4 text-left transition-all duration-200 min-h-[72px] sm:min-h-[86px]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        isSelected
          ? "border-accent-blue/50 bg-accent-blue/10"
          : isInvalid
          ? "border-red-500/50 bg-red-500/[0.06] ring-1 ring-red-500/30"
          : "border-white/[0.07] bg-foreground/[0.03] hover:border-primary/25"
      )}
    >
      {/* Required indicator strip */}
      {isInvalid && (
        <span className="absolute left-0 top-3 bottom-3 w-0.5 rounded-full bg-red-500" />
      )}
      <div className="flex-1 min-w-0 pr-2">
        <p className={cn(
          "text-[9px] sm:text-[10px] font-bold uppercase tracking-widest mb-1 sm:mb-1.5 transition-colors",
          isInvalid ? "text-red-400/80" : "text-muted-foreground/50"
        )}>
          {labels.title}
        </p>
        {isSelected ? (
          <div className="flex items-center gap-2">
            {selectedVoice!.style && (
              <span className={cn("w-1.5 h-1.5 rounded-full shrink-0 mt-0.5", accent.dot)} />
            )}
            <p className="text-sm font-semibold text-white leading-snug truncate">
              {selectedVoice!.name}
            </p>
          </div>
        ) : isInvalid ? (
          <p className="text-xs text-red-400/70 italic">{labels.choose}</p>
        ) : (
          <p className="text-xs text-muted-foreground/35 italic">{labels.empty}</p>
        )}
      </div>

      {/* Play / error icon */}
      <div className="shrink-0 self-center ml-2">
        {isSelected ? (
          <div
            onClick={toggle}
            role="button"
            aria-label={isPlaying ? labels.stopPreview : labels.playPreview}
            className={cn(
              "flex size-9 items-center justify-center rounded-full border transition-all duration-200",
              isPlaying
                ? "bg-primary border-primary text-primary-foreground"
                : "bg-primary/15 border-primary/30 text-primary hover:bg-primary hover:border-primary hover:text-primary-foreground"
            )}
          >
            {isLoading ? <Loader2 className="size-3.5 animate-spin" /> :
             isPlaying ? <Pause className="size-3.5" /> :
             <Play className="size-3.5 ml-0.5" />}
          </div>
        ) : isInvalid ? (
          <span className="flex size-9 items-center justify-center opacity-70">
            <Icon name="attention-circle" size={16} className="size-4 text-red-400" />
          </span>
        ) : (
          <span className="flex size-9 items-center justify-center text-muted-foreground/25 group-hover:text-muted-foreground/45 transition-colors">
            <Icon name="voice" size={20} className="size-5" />
          </span>
        )}
      </div>
    </button>
  )
}

// ─── Voice Card (inside dialog) ───────────────────────────────────────────────

function VoiceCard({
  voice, isSelected, onSelect,
}: {
  voice: Voice; isSelected: boolean; onSelect: () => void
}) {
  const src = voice.previewUrl ?? null
  const { isPlaying, isLoading, hasError, toggle } = useAudioPlayer(src)
  const accent = getAccent(voice.style)
  const tags = [voice.accent, voice.style].filter(Boolean) as string[]

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative flex flex-col gap-2 rounded-xl border p-3 sm:gap-3 sm:rounded-2xl sm:p-4 text-left transition-all duration-200 w-full",
        isSelected
          ? `border-accent-blue/50 bg-accent-blue/10 ring-1 ${accent.ring}`
          : "border-white/[0.08] bg-foreground/[0.03] hover:border-primary/25"
      )}
    >
      {/* Color dot + name + play */}
      <div className="flex items-center gap-2 sm:gap-2.5">
        <span className={cn("w-2 h-2 rounded-full shrink-0", accent.dot)} />
        <div className="flex-1 min-w-0">
          <p className={cn("font-bold text-xs sm:text-sm", isSelected ? "text-accent-blue" : "text-foreground")}>
            {voice.name}
          </p>
          {voice.description && (
            <p className="text-[10px] sm:text-[11px] text-muted-foreground/65 mt-0.5 leading-snug line-clamp-1 sm:line-clamp-2">
              {voice.description}
            </p>
          )}
        </div>
        {isSelected && (
          <span className="flex size-4 sm:size-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Icon name="tick" size={12} className="size-2.5 sm:size-3" />
          </span>
        )}
      </div>

      {/* Tags + play button */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <span key={tag} className={cn(
              "px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold capitalize",
              isSelected ? accent.badge : "bg-white/[0.06] text-muted-foreground/55"
            )}>
              {tag}
            </span>
          ))}
        </div>

        {/* Play button */}
        <div
          onClick={toggle}
          role="button"
          aria-label={isPlaying ? "Stop" : hasError ? "Preview unavailable" : "Play sample"}
          className={cn(
            "flex size-8 sm:size-9 shrink-0 items-center justify-center rounded-full border transition-all duration-200",
            hasError
              ? "border-red-500/30 bg-red-500/10 text-red-400 cursor-not-allowed"
              : isPlaying
              ? "bg-primary border-primary text-primary-foreground"
              : isSelected
              ? "bg-primary/15 border-primary/40 text-primary hover:bg-primary hover:border-primary hover:text-primary-foreground"
              : "border-white/10 bg-white/[0.04] text-muted-foreground/60 hover:border-primary/40 hover:text-primary"
          )}
        >
          {isLoading ? <Loader2 className="size-3 sm:size-3.5 animate-spin" /> :
           hasError ? <Icon name="attention-circle" size={14} className="size-3 sm:size-3.5" /> :
           isPlaying ? <Pause className="size-3 sm:size-3.5" /> :
           <Play className="size-3 sm:size-3.5 ml-0.5" />}
        </div>
      </div>
    </button>
  )
}

// ─── Voice Selector Dialog ────────────────────────────────────────────────────

function VoiceSelectorDialog({
  open, onOpenChange, voices, selectedId, onSelect, labels,
}: {
  open: boolean; onOpenChange: (open: boolean) => void
  voices: Voice[]; selectedId: string; onSelect: (id: string) => void
  labels: {
    title: string
    description: string
    close: string
  }
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-0 border-white/[0.08] bg-card overflow-hidden p-0 top-auto bottom-0 left-0 right-0 translate-x-0 translate-y-0 max-w-full rounded-b-none rounded-t-3xl max-h-[75vh] sm:top-[50%] sm:bottom-auto sm:left-[50%] sm:right-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:max-w-lg sm:rounded-2xl sm:max-h-[85vh]"
      >
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        <DialogHeader className="px-5 pt-3 sm:pt-5 pb-3 sm:pb-4 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-3">
            <span className="flex size-9 sm:size-10 items-center justify-center rounded-xl bg-primary/10 text-primary border border-primary/20">
              <Icon name="voice" size={16} className="size-4 sm:size-5" />
            </span>
            <div>
              <DialogTitle className="text-sm sm:text-base font-bold text-white">{labels.title}</DialogTitle>
              <DialogDescription className="text-[11px] sm:text-xs text-muted-foreground/70 mt-0.5">
                {labels.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="grid grid-cols-2 gap-2 p-4 sm:gap-3 sm:p-5">
            {voices.map((voice) => (
              <VoiceCard
                key={voice.id}
                voice={voice}
                isSelected={selectedId === voice.id}
                onSelect={() => { onSelect(voice.id); onOpenChange(false) }}
              />
            ))}
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-5 py-3 sm:py-4 border-t border-white/[0.06] shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-muted-foreground hover:text-white"
          >
            {labels.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Option Selector Dialog ───────────────────────────────────────────────────

const ACCENT_ICON_BG: Record<string, string> = {
  blue:    "bg-primary/10 text-primary border-primary/20",
  violet:  "bg-violet-500/15 text-violet-400 border-violet-500/20",
  rose:    "bg-rose-500/15 text-rose-400 border-rose-500/20",
  emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  pink:    "bg-pink-500/15 text-pink-400 border-pink-500/20",
  amber:   "bg-amber-500/15 text-amber-400 border-amber-500/20",
}

function OptionSelectorDialog({
  open, onOpenChange, title, description, icon, accentColor = "blue",
  options, selected, onConfirm, multi = false, maxSelect = 5,
  emptyValueLabel = "None", customSlot, customLabel = "Custom", cancelLabel = "Cancel", saveLabel = "Save",
}: {
  open: boolean; onOpenChange: (open: boolean) => void
  title: string; description?: string; icon?: React.ReactNode
  accentColor?: "blue" | "pink" | "violet" | "amber" | "emerald" | "rose"
  options: ReadonlyArray<ChipOption>; selected: string | string[]
  onConfirm: (value: string | string[]) => void
  multi?: boolean; maxSelect?: number; emptyValueLabel?: string
  customLabel?: string; cancelLabel?: string; saveLabel?: string
  customSlot?: React.ReactNode
}) {
  const [local, setLocal] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setLocal(Array.isArray(selected) ? [...selected] : selected ? [selected] : [])
    }
  }, [open, selected])

  const handleToggle = (value: string) => {
    if (multi) {
      if (value === "none" || value === "") { setLocal([]); return }
      if (local.includes(value)) {
        setLocal((p) => p.filter((v) => v !== value))
      } else if (local.length < maxSelect) {
        setLocal((p) => [...p, value])
      }
    } else {
      onConfirm(value === "none" ? "" : value)
      onOpenChange(false)
    }
  }

  const displayOptions = options.filter((o) => o.value !== "none")
  const hasNone = options.some((o) => o.value === "none")
  const iconBg = ACCENT_ICON_BG[accentColor] ?? ACCENT_ICON_BG.blue

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col gap-0 border-white/[0.08] bg-card overflow-hidden p-0 top-auto bottom-0 left-0 right-0 translate-x-0 translate-y-0 max-w-full rounded-b-none rounded-t-3xl max-h-[75vh] sm:top-[50%] sm:bottom-auto sm:left-[50%] sm:right-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:max-w-md sm:rounded-2xl sm:max-h-[85vh]"
      >
        {/* Mobile drag handle */}
        <div className="flex justify-center pt-3 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        <DialogHeader className="px-4 sm:px-5 pt-2 sm:pt-5 pb-3 sm:pb-4 border-b border-white/[0.06] shrink-0">
          <div className="flex items-center gap-3">
            <span className={cn("flex size-9 sm:size-10 items-center justify-center rounded-xl border", iconBg)}>
              {icon ?? <Icon name="flash" size={16} className="size-4 sm:size-5" />}
            </span>
            <div className="flex-1">
              <DialogTitle className="text-sm sm:text-base font-bold text-white">{title}</DialogTitle>
              {description && (
                <DialogDescription className="text-[11px] sm:text-xs text-muted-foreground/70 mt-0.5">
                  {description}
                </DialogDescription>
              )}
            </div>
            {multi && (
              <span className="px-2 sm:px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-bold bg-primary/10 text-primary border border-primary/20">
                {local.length}/{maxSelect}
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
            {/* None option */}
            {hasNone && !multi && (
              <button
                type="button"
                onClick={() => { onConfirm(""); onOpenChange(false) }}
                className={cn(
                  "flex items-center gap-2 rounded-full px-3.5 sm:px-4 py-2 text-sm font-semibold transition-all min-h-[38px] sm:min-h-[40px] border",
                  local.length === 0
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-white/[0.08] bg-white/[0.03] text-muted-foreground hover:border-white/15 hover:text-foreground"
                )}
              >
                <span>🚫</span> {emptyValueLabel}
              </button>
            )}

            {/* Chips */}
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {displayOptions.map(({ value, label, emoji }) => {
                const isActive = local.includes(value)
                const isDisabled = !isActive && multi && local.length >= maxSelect
                return (
                  <Chip
                    key={value}
                    value={value} label={label} emoji={emoji}
                    isActive={isActive} isDisabled={isDisabled}
                    onToggle={handleToggle}
                  />
                )
              })}
            </div>

            {/* Custom option (inside dialog) */}
            {customSlot && (
              <div className="pt-3 border-t border-white/[0.06]">
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/45 mb-2.5">
                  {customLabel}
                </p>
                {customSlot}
              </div>
            )}
          </div>
        </div>

        {/* Footer: only for multi-select */}
        {multi && (
          <DialogFooter className="px-4 sm:px-5 py-3 sm:py-4 border-t border-white/[0.06] shrink-0 gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="border-white/10 bg-white/[0.04] hover:bg-white/[0.08] text-muted-foreground hover:text-white"
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => { onConfirm(local); onOpenChange(false) }}
              className="font-semibold"
            >
              {saveLabel} {local.length > 0 && `(${local.length})`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPersonalitySummary(personality: string[], options: ReadonlyArray<ChipOption>) {
  if (!personality?.length) return ""
  return personality.slice(0, 5).map((v) => options.find((o) => o.value === v)?.label ?? v).join(" · ")
}

function getSingleLabel(value: string, options: ReadonlyArray<ChipOption>) {
  return options.find((o) => o.value === value)?.label ?? value
}

function getEmoji(value: string, options: ReadonlyArray<ChipOption>) {
  return options.find((o) => o.value === value)?.emoji ?? ""
}

// ─── Main Component ───────────────────────────────────────────────────────────

const errorInputClass = "border-red-500/60 ring-1 ring-red-500/25 bg-red-500/[0.04] focus:border-red-500/60 focus:ring-red-500/30"

export function PersonalityStep({ invalidFields = [] }: { invalidFields?: string[] }) {
  const t = useTranslations("create.wizard.personalityStep")
  const wizardT = useTranslations("create.wizard")
  const tOptions = useTranslations("create.wizard.optionLabels")
  const { draft, updateDraft } = useCharacterStore()
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = !!user
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const registerModalDismissed = useUIStore((s) => s.registerModalDismissed)
  const isPremium = isPaidTier(user?.subscription ?? "free")
  const isNameInvalid = invalidFields.includes("name")
  const isVoiceInvalid = invalidFields.includes("voiceId")
  const personalityOptions = localizeCreateOptions(PERSONALITY_CHIP_DATA, "personality", tOptions)
  const occupationOptions = localizeCreateOptions(OCCUPATION_OPTIONS, "occupation", tOptions)
  const relationshipOptions = localizeCreateOptions(RELATIONSHIP_OPTIONS, "relationship", tOptions)
  const hobbyOptions = localizeCreateOptions(HOBBY_OPTIONS, "hobby", tOptions)
  const fetishOptions = localizeCreateOptions(FETISH_OPTIONS, "fetish", tOptions)

  const [showAdvanced, setShowAdvanced] = useState(false)
  const [isGeneratingBackstory, setIsGeneratingBackstory] = useState(false)

  const [dialogOpen, setDialogOpen] = useState<
    "voice" | "personality" | "occupation" | "relationship" | "hobby" | "fetish" | null
  >(null)

  const { data: voices } = useQuery({
    queryKey: ["ai_assets", "tts_voice", "active"],
    queryFn: async () => {
      const supabase = createClient()
      const { data } = await supabase
        .from("ai_assets")
        .select("id, name, description, thumbnail_url, preview_urls, type_config, providers:ai_asset_providers(provider_model_id, config, priority, is_active)")
        .eq("asset_type", "tts_voice")
        .eq("status", "active")
        .order("name")
      return data ?? []
    },
    staleTime: 5 * 60 * 1000,
  })

  const voiceList: Voice[] =
    voices && voices.length > 0
      ? voices.map((v) => {
          const tc = v.type_config as VoiceTypeConfig | null
          return {
            id: v.id, name: v.name, description: v.description,
            previewUrl: v.preview_urls?.[0] ?? null, accent: tc?.accent ?? null, style: tc?.style ?? null,
          }
        })
      : VOICE_FALLBACK_OPTIONS.map((v) => ({
          id: v.id, name: v.name, description: v.description,
          previewUrl: null, accent: v.accent ?? null, style: v.style ?? null,
        }))

  const liveVoicesLoaded = Boolean(voices?.length)

  // Keep a valid voice when live assets load (fallback ids differ from Supabase UUIDs).
  useEffect(() => {
    const voiceId = reconcileVoiceId(draft.voiceId, voiceList, liveVoicesLoaded, pickRandomVoiceId)
    if (voiceId) updateDraft({ voiceId })
  }, [voiceList, liveVoicesLoaded, draft.voiceId, updateDraft])

  const isCustomOccupation = draft.occupation !== "" && !OCCUPATION_OPTIONS.some((o) => o.value === draft.occupation)
  const isCustomHobby = draft.hobby !== "" && !HOBBY_OPTIONS.some((o) => o.value === draft.hobby)
  const isCustomFetish = draft.fetish !== "" && !FETISH_OPTIONS.some((o) => o.value === draft.fetish)

  const personalitySummary = getPersonalitySummary((draft.personality as string[]) ?? [], personalityOptions)
  const occupationSummary = isCustomOccupation ? draft.occupation : getSingleLabel(draft.occupation, occupationOptions)
  const occupationEmoji = isCustomOccupation ? "💼" : getEmoji(draft.occupation, occupationOptions)
  const relationshipSummary = getSingleLabel(draft.relationshipType, relationshipOptions)
  const relationshipEmoji = getEmoji(draft.relationshipType, relationshipOptions)
  const hobbySummary = isCustomHobby ? draft.hobby : getSingleLabel(draft.hobby, hobbyOptions)
  const hobbyEmoji = isCustomHobby ? "🎯" : getEmoji(draft.hobby, hobbyOptions)
  const fetishSummary = isCustomFetish ? draft.fetish : getSingleLabel(draft.fetish, fetishOptions)
  const fetishEmoji = isCustomFetish ? "💫" : getEmoji(draft.fetish, fetishOptions)
  const selectedVoice = voiceList.find((v) => v.id === draft.voiceId) ?? null

  return (
    <div className="space-y-5">
      {/* Character Name — always first */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label
            htmlFor="personality-name"
            className={cn("text-foreground font-semibold text-sm transition-colors", isNameInvalid && "text-red-400")}
          >
            {t("nameLabel")} <span className="text-red-400">*</span>
          </Label>
          {isNameInvalid && (
            <span className="flex items-center gap-1 text-xs text-red-400">
              <Icon name="attention-circle" size={12} className="size-3" />
              {t("nameMinError")}
            </span>
          )}
        </div>
        <Input
          id="personality-name"
          placeholder={t("namePlaceholder")}
          value={draft.name}
          onChange={(e) => updateDraft({ name: e.target.value })}
          className={cn(
            "h-12 rounded-2xl bg-foreground/[0.03] border-white/[0.08] text-base font-semibold placeholder:text-muted-foreground/35 focus:border-primary/50 focus:bg-foreground/[0.03] transition-all",
            isNameInvalid && errorInputClass
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <VoiceAttributeCard
          selectedVoice={selectedVoice}
          onClick={() => setDialogOpen("voice")}
          isInvalid={isVoiceInvalid}
          labels={{
            title: t("voiceLabelRequired"),
            choose: t("voiceChooseHint"),
            empty: t("voiceEmpty"),
            stopPreview: t("stopPreview"),
            playPreview: t("playPreview"),
          }}
        />

        <AttributeCard
          label={wizardT("fieldLabels.personality")} value={personalitySummary} placeholder={t("personalityEmpty")}
          emoji={personalitySummary ? "✨" : undefined}
          defaultIcon={<Icon name="flash" size={16} className="size-4 text-muted-foreground" />}
          isSelected={!!personalitySummary} onClick={() => setDialogOpen("personality")}
        />
      </div>

      {isVoiceInvalid && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-3.5 py-2.5">
          <Icon name="attention-circle" size={16} className="size-4 shrink-0 text-red-400" />
          <p className="text-sm text-red-400">
            {t("voiceRequiredError")}
          </p>
        </div>
      )}

      {/* Description (optional) — how the character describes themselves, like Character.AI */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label
            htmlFor="personality-description"
            className="text-foreground font-semibold text-sm transition-colors"
          >
            {t("descriptionLabel")} <span className="text-muted-foreground font-normal">({wizardT("common.optional")})</span>
          </Label>
        </div>
        <label htmlFor="personality-description" className="block text-xs text-muted-foreground cursor-text">
          {t("descriptionHelp")}
        </label>
        <Textarea
          id="personality-description"
          value={draft.description}
          onChange={(e) => updateDraft({ description: e.target.value })}
          placeholder={t("descriptionPlaceholder")}
          className="min-h-[72px] rounded-xl bg-foreground/[0.03] border-white/[0.08] text-sm placeholder:text-muted-foreground/35 focus:border-primary/50 focus:bg-foreground/[0.03] transition-all resize-none"
          maxLength={2000}
        />
        <p className="text-xs text-muted-foreground text-right">{draft.description.length}/2000</p>
      </div>

      {/* More options — every field in here is optional or already pre-filled */}
      <div className="rounded-xl border border-border/30 bg-card/30 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          aria-expanded={showAdvanced}
          className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-foreground hover:bg-white/[0.02] transition-colors"
        >
          <span>More options <span className="text-muted-foreground font-normal">(optional)</span></span>
          <Icon
            name="arrow-down-2"
            size={16}
            className={cn(
              "size-4 text-muted-foreground transition-transform duration-200",
              showAdvanced && "rotate-180"
            )}
          />
        </button>

        {showAdvanced && (
          <div className="space-y-4 px-4 pb-4 border-t border-border/20 pt-4">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <AttributeCard
                label={wizardT("fieldLabels.occupation")} value={occupationSummary} placeholder={t("occupationEmpty")}
                emoji={occupationEmoji || undefined}
                defaultIcon={<Icon name="bag" size={16} className="size-4 text-muted-foreground" />}
                isSelected={!!occupationSummary} onClick={() => setDialogOpen("occupation")}
              />
              <AttributeCard
                label={wizardT("fieldLabels.relationship")} value={relationshipSummary} placeholder={t("relationshipEmpty")}
                emoji={relationshipEmoji || undefined}
                defaultIcon={<Icon name="heart" size={16} className="size-4 text-muted-foreground" />}
                isSelected={!!relationshipSummary} onClick={() => setDialogOpen("relationship")}
              />
              <AttributeCard
                label={wizardT("fieldLabels.hobby")} value={hobbySummary} placeholder={t("hobbyEmpty")}
                emoji={hobbyEmoji || undefined}
                defaultIcon={<Icon name="monitor-1" size={16} className="size-4 text-muted-foreground" />}
                isSelected={!!hobbySummary} onClick={() => setDialogOpen("hobby")}
              />
              <AttributeCard
                label={wizardT("fieldLabels.fetish")} value={fetishSummary} placeholder={t("fetishEmpty")}
                emoji={fetishEmoji || undefined}
                defaultIcon={<Icon name="flash" size={16} className="size-4 text-muted-foreground" />}
                isSelected={!!fetishSummary} onClick={() => setDialogOpen("fetish")}
              />
            </div>

            {/* Definition — long-form, high impact on responses (stored as backstory) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label htmlFor="personality-definition" className="text-foreground font-medium text-sm">Definition</Label>
                <button
                  type="button"
                  onClick={async () => {
                    if (!isAuthenticated) {
                      // Same auth-gate contract as the generation and custom-option
                      // gates: always instrument the block, and after the register
                      // modal has been dismissed once this session fall back to an
                      // inline prompt instead of re-popping the modal on every click.
                      track("character:creation_blocked_unauthenticated", { gate: "ai_autocomplete" })
                      if (registerModalDismissed) {
                        toast.info("Log in to autocomplete with AI")
                      } else {
                        setActiveModal("register")
                      }
                      return
                    }
                    if (!draft.name || draft.name.length < 2) {
                      toast.error("Please enter a character name first")
                      return
                    }
                    setIsGeneratingBackstory(true)
                    try {
                      const result = await generateBackstory({
                        name: draft.name,
                        gender: (draft.gender || "female") as string,
                        age: draft.age,
                        ethnicity: draft.ethnicity,
                        personality: draft.personality as string[],
                        style: (draft.style || "realistic") as string,
                        occupation: draft.occupation || undefined,
                        relationshipType: draft.relationshipType || undefined,
                        hobby: draft.hobby || undefined,
                      })
                      if (result.success && result.backstory) {
                        updateDraft({ backstory: result.backstory })
                        toast.success("Definition generated!")
                      } else {
                        toast.error(result.error || "Failed to generate definition")
                      }
                    } catch {
                      toast.error("Something went wrong generating definition")
                    } finally {
                      setIsGeneratingBackstory(false)
                    }
                  }}
                  disabled={isGeneratingBackstory}
                  className="flex items-center gap-2 rounded-full bg-primary/10 border border-primary/30 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/20 transition-all disabled:opacity-50"
                >
                  {isGeneratingBackstory ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Icon name="flash" size={16} className="size-4" />
                  )}
                  Autocomplete With AI
                </button>
              </div>
              <label htmlFor="personality-definition" className="block text-xs text-muted-foreground cursor-text">
                Long-form character definition. Has a strong impact on how the character responds. Optional but recommended for deeper, more consistent replies.
              </label>
              <Textarea
                id="personality-definition"
                value={draft.backstory}
                onChange={(e) => updateDraft({ backstory: e.target.value })}
                placeholder="Detailed personality, backstory, how they speak, relationships, scenario notes, example dialogues..."
                className="min-h-[160px] bg-muted/30 border-border/30 focus:border-primary/50 resize-none text-sm"
                maxLength={32000}
              />
              <p className="text-xs text-muted-foreground text-right">{draft.backstory.length}/32,000</p>
            </div>

            {/* Greeting */}
            <div className="space-y-2">
              <Label htmlFor="personality-greeting" className="text-foreground font-medium text-sm">Greeting</Label>
              <Textarea
                id="personality-greeting"
                value={draft.greeting}
                onChange={(e) => updateDraft({ greeting: e.target.value })}
                placeholder="The first thing your character says when meeting someone..."
                className="min-h-[80px] bg-muted/30 border-border/30 focus:border-primary/50 resize-none text-sm"
                maxLength={500}
              />
            </div>

            {/* Scenario */}
            <div className="space-y-2">
              <Label htmlFor="personality-scenario" className="text-foreground font-medium text-sm">Scenario</Label>
              <Textarea
                id="personality-scenario"
                value={draft.scenario}
                onChange={(e) => updateDraft({ scenario: e.target.value })}
                placeholder="Set the scene — where and how do conversations typically start..."
                className="min-h-[80px] bg-muted/30 border-border/30 focus:border-primary/50 resize-none text-sm"
                maxLength={500}
              />
            </div>

            {/* Custom Physical Details */}
            <div className="space-y-2">
              <Label htmlFor="personality-physical" className="text-foreground font-medium text-sm">Custom Physical Details</Label>
              <Textarea
                id="personality-physical"
                value={draft.customPhysicalDetails}
                onChange={(e) => updateDraft({ customPhysicalDetails: e.target.value })}
                placeholder="Any additional physical traits not covered above..."
                className="min-h-[80px] bg-muted/30 border-border/30 focus:border-primary/50 resize-none text-sm"
                maxLength={1000}
              />
            </div>

            {/* Additional Personality Details */}
            <div className="space-y-2">
              <Label htmlFor="personality-additional" className="text-foreground font-medium text-sm">Additional Personality Details</Label>
              <Textarea
                id="personality-additional"
                value={draft.additionalPersonalityDetails}
                onChange={(e) => updateDraft({ additionalPersonalityDetails: e.target.value })}
                placeholder="Deeper personality description, quirks, preferences..."
                className="min-h-[80px] bg-muted/30 border-border/30 focus:border-primary/50 resize-none text-sm"
                maxLength={1000}
              />
            </div>

            {/* Extra Details */}
            <div className="space-y-2">
              <Label htmlFor="personality-extra" className="text-foreground font-medium text-sm">Extra Details</Label>
              <Textarea
                id="personality-extra"
                value={draft.extraDetails}
                onChange={(e) => updateDraft({ extraDetails: e.target.value })}
                placeholder="Anything else about your character..."
                className="min-h-[80px] bg-muted/30 border-border/30 focus:border-primary/50 resize-none text-sm"
                maxLength={1000}
              />
            </div>
          </div>
        )}
      </div>

      {/* Visibility — small, at end */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <Label className="text-muted-foreground text-sm shrink-0">Visibility</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                "border-white/[0.08] bg-foreground/[0.03] hover:border-white/[0.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              )}
            >
              {draft.isPublic ? (
                <Icon name="monitor" size={14} className="size-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <Icon name="lock" size={14} className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              <span>{draft.isPublic ? "Public" : "Private"}</span>
              <Icon name="arrow-down-2" size={14} className="size-3.5 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[140px] border-white/[0.08] bg-[#0a0f1a]">
            <DropdownMenuItem onClick={() => updateDraft({ isPublic: true })}>
              <Icon name="monitor" size={16} className="size-4 mr-2" />
              Public
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => updateDraft({ isPublic: false })}>
              <Icon name="lock" size={16} className="size-4 mr-2" />
              Private
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Dialogs */}
      <VoiceSelectorDialog
        open={dialogOpen === "voice"}
        onOpenChange={(open) => !open && setDialogOpen(null)}
        voices={voiceList} selectedId={draft.voiceId}
        onSelect={(id) => updateDraft({ voiceId: id })}
        labels={{
          title: t("voiceDialogTitle"),
          description: t("voiceDialogDescription"),
          close: wizardT("common.close"),
        }}
      />

      <OptionSelectorDialog
        open={dialogOpen === "personality"}
        onOpenChange={(open) => !open && setDialogOpen(null)}
        title={wizardT("fieldLabels.personality")} description={t("personalityDialogDescription")}
        icon={<Icon name="flash" size={20} className="size-5" />} accentColor="violet"
        options={personalityOptions} selected={(draft.personality as string[]) ?? []}
        onConfirm={(v) => updateDraft({ personality: (v as string[]) as CharacterPersonality[] })}
        multi maxSelect={5}
        cancelLabel={wizardT("common.cancel")}
        saveLabel={wizardT("common.save")}
      />

      <OptionSelectorDialog
        open={dialogOpen === "occupation"}
        onOpenChange={(open) => !open && setDialogOpen(null)}
        title={wizardT("fieldLabels.occupation")} description={t("occupationDialogDescription")}
        icon={<Icon name="bag" size={20} className="size-5" />} accentColor="blue"
        options={occupationOptions}
        selected={isCustomOccupation ? "" : draft.occupation}
        onConfirm={(v) => updateDraft({ occupation: (v as string) === "none" ? "" : (v as string) })}
        emptyValueLabel={wizardT("common.none")}
        customLabel={wizardT("common.custom")}
        customSlot={
          <PremiumCustomOption
            isAuthenticated={isAuthenticated} isPremium={isPremium}
            isSelected={isCustomOccupation}
            customValue={isCustomOccupation ? draft.occupation : ""}
            onCustomChange={(v) => updateDraft({ occupation: v })}
            placeholder={t("customOccupationPlaceholder")}
            dialogTitle={t("customOccupationTitle")} variant="chip"
            onBeforeOpenModal={() => setDialogOpen(null)}
          />
        }
      />

      <OptionSelectorDialog
        open={dialogOpen === "relationship"}
        onOpenChange={(open) => !open && setDialogOpen(null)}
        title={wizardT("fieldLabels.relationship")} description={t("relationshipDialogDescription")}
        icon={<Icon name="heart" size={20} className="size-5" />} accentColor="rose"
        options={relationshipOptions} selected={draft.relationshipType}
        onConfirm={(v) => updateDraft({ relationshipType: v as string })}
      />

      <OptionSelectorDialog
        open={dialogOpen === "hobby"}
        onOpenChange={(open) => !open && setDialogOpen(null)}
        title={wizardT("fieldLabels.hobby")} description={t("hobbyDialogDescription")}
        icon={<Icon name="monitor-1" size={20} className="size-5" />} accentColor="emerald"
        options={hobbyOptions}
        selected={isCustomHobby ? "" : draft.hobby}
        onConfirm={(v) => updateDraft({ hobby: (v as string) === "none" ? "" : (v as string) })}
        emptyValueLabel={wizardT("common.none")}
        customLabel={wizardT("common.custom")}
        customSlot={
          <PremiumCustomOption
            isAuthenticated={isAuthenticated} isPremium={isPremium}
            isSelected={isCustomHobby}
            customValue={isCustomHobby ? draft.hobby : ""}
            onCustomChange={(v) => updateDraft({ hobby: v })}
            placeholder={t("customHobbyPlaceholder")}
            dialogTitle={t("customHobbyTitle")} variant="chip"
            onBeforeOpenModal={() => setDialogOpen(null)}
          />
        }
      />

      <OptionSelectorDialog
        open={dialogOpen === "fetish"}
        onOpenChange={(open) => !open && setDialogOpen(null)}
        title={wizardT("fieldLabels.fetish")} description={t("fetishDialogDescription")}
        icon={<Icon name="flash" size={20} className="size-5" />} accentColor="pink"
        options={fetishOptions}
        selected={isCustomFetish ? "" : draft.fetish}
        onConfirm={(v) => updateDraft({ fetish: (v as string) === "none" ? "" : (v as string) })}
        emptyValueLabel={wizardT("common.none")}
        customLabel={wizardT("common.custom")}
        customSlot={
          <PremiumCustomOption
            isAuthenticated={isAuthenticated} isPremium={isPremium}
            isSelected={isCustomFetish}
            customValue={isCustomFetish ? draft.fetish : ""}
            onCustomChange={(v) => updateDraft({ fetish: v })}
            placeholder={t("customFetishPlaceholder")}
            dialogTitle={t("customFetishTitle")} variant="chip"
            onBeforeOpenModal={() => setDialogOpen(null)}
          />
        }
      />
    </div>
  )
}
